import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Building2 } from "lucide-react";
import { api } from "@/api";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";

const CATEGORIES = [
  "General Enquiry", "The Topper Academy", "Indian School of Music", "Careers",
  "Partnerships", "Investor Relations", "Media", "Corporate Enquiry",
];

const inputCls =
  "w-full rounded-lg border border-navy-900/15 bg-white px-4 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 focus:border-gold-dark focus:outline-none focus:ring-2 focus:ring-gold/30";

function ContactForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", organisation: "", designation: "",
    category: "General Enquiry", subject: "", message: "", consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please complete name, email and message.");
      return;
    }
    if (!form.consent) {
      toast.error("Please provide consent to be contacted.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      setSubmitted(true);
      toast.success("Your enquiry has been submitted.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit your enquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-2xl border border-navy-900/10 bg-white p-8 py-12 text-center lg:p-10" data-testid="contact-success">
        <h3 className="font-serif text-2xl text-navy-900">Thank you for reaching out.</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-slate-600">
          Your enquiry has been received by the RCS team. We will respond as soon as possible.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-navy-900/10 bg-white p-8 lg:p-10">
      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <input required aria-label="Name" placeholder="Name *" className={inputCls} value={form.name} onChange={set("name")} data-testid="contact-name" />
          <input required type="email" aria-label="Email" placeholder="Email *" className={inputCls} value={form.email} onChange={set("email")} data-testid="contact-email" />
          <input aria-label="Phone" placeholder="Phone" className={inputCls} value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
          <input aria-label="Organisation" placeholder="Organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} data-testid="contact-organisation" />
          <input aria-label="Designation" placeholder="Designation" className={inputCls} value={form.designation} onChange={set("designation")} data-testid="contact-designation" />
          <select aria-label="Enquiry category" className={inputCls} value={form.category} onChange={set("category")} data-testid="contact-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <input aria-label="Subject" placeholder="Subject" className={inputCls} value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
        <textarea required aria-label="Message" placeholder="Message *" rows={5} className={inputCls} value={form.message} onChange={set("message")} data-testid="contact-message" />
        <label className="flex items-start gap-3 text-xs leading-relaxed text-slate-600" data-testid="contact-consent-label">
          <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 h-4 w-4 accent-[#C5A059]" data-testid="contact-consent" />
          I consent to Roy Career Solutions Private Limited storing and processing my details to respond to this enquiry.
        </label>
        <button type="submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="contact-submit">
          {submitting ? "Submitting…" : "Submit Enquiry"}
        </button>
      </form>
    </div>
  );
}

export default function Contact() {
  const [company, setCompany] = useState(null);

  useEffect(() => {
    api.get("/company").then((r) => setCompany(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="contact-page">
      <PageHero
        eyebrow="Contact"
        title="Let's Build Something Meaningful Together."
        lede="Whether you are a learner, educator, partner, investor or journalist — we would like to hear from you."
      />

      <section className="py-24 lg:py-32" data-testid="contact-section">
        <div className="container-rcs grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="eyebrow">Reach Us</h2>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4 rounded-2xl border border-navy-900/10 bg-white p-6" data-testid="contact-location">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy-950 text-gold">
                    <MapPin className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-navy-900">Corporate Location</h3>
                    <p className="mt-1 text-sm text-slate-600">{company?.corporateLocation || "Bengaluru, Karnataka, India"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 rounded-2xl border border-navy-900/10 bg-white p-6" data-testid="contact-company">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy-950 text-gold">
                    <Building2 className="h-5 w-5" aria-hidden />
                  </span>
                  <div>
                    <h3 className="font-serif text-lg text-navy-900">{company?.legalName || "Roy Career Solutions Private Limited"}</h3>
                    <p className="mt-1 font-mono text-xs tracking-wider text-slate-500">CIN: {company?.cin || "U85500KA2024PTC194012"}</p>
                    {company?.email && <p className="mt-2 text-sm text-slate-600">{company.email}</p>}
                    {company?.phone && <p className="mt-1 text-sm text-slate-600">{company.phone}</p>}
                  </div>
                </div>
                <p className="text-xs leading-relaxed text-slate-500" data-testid="corporate-enquiries-note">
                  For corporate & strategic enquiries — investors, institutional collaborations and strategic
                  alliances — please select “Investor Relations” or “Corporate Enquiry” in the form.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            <ContactForm />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
